import React from "react";
import { Platform } from "react-native";
import { ControllerRenderProps, FieldValues, Path } from "react-hook-form";

import {
  BaseFormInputPropsType,
  VnFormControl,
  VnFormDescription,
  VnFormErrorMessage,
  VnFormField,
  VnFormItem,
  VnFormLabel,
} from "@/components/VnForm";
import { OptionType } from "@/domain/common";
import { I18nKeyType } from "@/i18n";
import { cn } from "@/utils/tailwind";

import { VnBox } from "../VnBox";
import { TextVariantType } from "../VnText/variants";
import { VnTouchableOpacity } from "../VnTouchableOpacity";
import { BaseCheckboxInput } from "./BaseCheckboxInput";

type CheckboxOptionType = Omit<OptionType, "value"> & {
  descriptionI18nKey?: I18nKeyType;
  descriptionI18nOptions?: object;
};

type AbCheckboxGroupInputPropsType<T extends FieldValues> =
  BaseFormInputPropsType<T> & {
    hasMultipleOptions?: boolean;
    inputClassName?: string;
    onCheckedChange?: (checked: boolean, value?: string) => void;
    /**
     * Multi-option lists only. Runs before a toggle reaches the form; return
     * false to skip the default update when the change has to be confirmed or
     * applied elsewhere first.
     */
    onBeforeOptionToggle?: (nextValues: string[]) => boolean;
    /**
     * Multi-option lists only. Renders below an option while it is checked.
     */
    renderCheckedOptionContent?: (value: string) => React.ReactNode;
    checkboxItemLabelVariant?: TextVariantType;
    shouldWrapOptionLabel?: boolean;
    groupClassName?: string;
  } & (
      | {
          hasMultipleOptions?: false;
          option: CheckboxOptionType;
          options?: never;
          descriptionI18nKey?: never;
          descriptionI18nOptions?: never;
        }
      | {
          hasMultipleOptions: true;
          options: (OptionType & {
            isDisabled?: boolean;
          })[];
          option?: never;
          descriptionI18nKey?: I18nKeyType;
          descriptionI18nOptions?: object;
        }
    );

export const VnCheckboxInput = <T extends FieldValues>({
  name,
  control,
  hasMultipleOptions = false,
  options,
  option,
  isDisabled = false,
  labelI18nKey,
  labelI18nOptions,
  descriptionI18nKey,
  descriptionI18nOptions,
  showError = true,
  errorI18nKey,
  errorI18nOptions,
  className,
  inputClassName,
  groupClassName,
  isRequired = true,
  onCheckedChange,
  onBeforeOptionToggle,
  renderCheckedOptionContent,
  checkboxItemLabelVariant,
  shouldWrapOptionLabel = false,
  ...props
}: AbCheckboxGroupInputPropsType<T>) => {
  const renderSingleOption = (field: ControllerRenderProps<T, Path<T>>) => {
    if (!option) return null;

    return (
      <VnFormItem
        className={cn(
          "flex flex-row gap-x-2.5 gap-y-0",
          shouldWrapOptionLabel ? "items-start" : "items-center",
        )}>
        <VnFormControl>
          <BaseCheckboxInput
            disabled={isDisabled}
            checked={field.value}
            onCheckedChange={value => {
              field.onChange(value);
              onCheckedChange?.(value);
            }}
            className={inputClassName}
            {...props}
          />
        </VnFormControl>
        <VnTouchableOpacity
          className={cn(
            "z-10 flex flex-col gap-y-1.5",
            shouldWrapOptionLabel && "min-w-0 flex-1",
          )}
          onPress={() => field.onChange(!field.value)}>
          <VnFormLabel
            isDisabled={isDisabled}
            isRequired={isRequired}
            variant={checkboxItemLabelVariant}
            className={cn(
              "leading-none",
              {
                "font-primary": Boolean(labelI18nKey),
              },
              shouldWrapOptionLabel && "min-w-0 flex-1 leading-tight",
            )}>
            {option.label}
          </VnFormLabel>
          {option.descriptionI18nKey && (
            <VnFormDescription
              i18nKey={option.descriptionI18nKey}
              i18nOptions={option.descriptionI18nOptions}
            />
          )}
        </VnTouchableOpacity>
      </VnFormItem>
    );
  };

  const renderMultipleOptions = () => {
    return (
      <>
        {descriptionI18nKey && (
          <VnFormDescription
            i18nKey={descriptionI18nKey}
            i18nOptions={descriptionI18nOptions}
          />
        )}

        <VnBox
          className={cn(
            "native:gap-4 web:gap-2 flex flex-col",
            groupClassName,
          )}>
          {options?.map(option => (
            <VnFormField
              name={name}
              key={option.value}
              control={control}
              render={({ field: { ref, ...field } }) => {
                const fieldValue = (field.value ?? []) as string[];
                const isChecked = fieldValue.includes(option.value);

                const handleToggle = () => {
                  const nextValues = isChecked
                    ? fieldValue.filter(
                        (value: string) => value !== option.value,
                      )
                    : [...fieldValue, option.value];

                  if (onBeforeOptionToggle?.(nextValues) === false) return;

                  onCheckedChange?.(!isChecked, option.value);
                  field.onChange(nextValues);
                };

                return (
                  <VnFormItem>
                    <VnTouchableOpacity
                      onPress={handleToggle}
                      shouldDebounce={false}
                      disabled={isDisabled || option.isDisabled}
                      className={cn(
                        "flex flex-row items-center gap-x-2.5 gap-y-0 rounded-md",
                        shouldWrapOptionLabel && "items-start",
                        Platform.select({
                          web: "cursor-pointer",
                        }),
                      )}>
                      <VnFormControl>
                        <BaseCheckboxInput
                          disabled={isDisabled || option.isDisabled}
                          checked={isChecked}
                          className={cn(
                            inputClassName,
                            "web:pointer-events-none",
                          )}
                          onCheckedChange={handleToggle}
                          {...props}
                        />
                      </VnFormControl>
                      <VnFormLabel
                        isDisabled={isDisabled || option.isDisabled}
                        variant={checkboxItemLabelVariant}
                        className={cn(
                          "leading-tight",
                          {
                            "font-primary": Boolean(labelI18nKey),
                          },
                          shouldWrapOptionLabel && "min-w-0 flex-1",
                        )}>
                        {option.label}
                      </VnFormLabel>
                    </VnTouchableOpacity>
                    {isChecked && renderCheckedOptionContent?.(option.value)}
                  </VnFormItem>
                );
              }}
            />
          ))}
        </VnBox>
      </>
    );
  };

  return (
    <VnFormField
      name={name}
      control={control}
      render={({ field }) => (
        <VnFormItem>
          <VnBox className={cn("gap-2", className)}>
            {labelI18nKey && (
              <VnFormLabel
                i18nKey={labelI18nKey}
                i18nOptions={labelI18nOptions}
                isRequired={isRequired}
              />
            )}
            {hasMultipleOptions
              ? renderMultipleOptions()
              : renderSingleOption(field)}
          </VnBox>
          {showError && (
            <VnFormErrorMessage
              i18nKey={errorI18nKey}
              i18nOptions={errorI18nOptions}
            />
          )}
        </VnFormItem>
      )}
    />
  );
};
